// The boat's menu (TOM-513), after the Neo home handoff's "Navigation": a
// panel that slides in from the left over a scrim, at every width, on every
// boat page. The left sidebar's More opens it above 768 px (ADR 0396 §5); a
// menu button in the header, or home's, opens it at 768 px and under. It holds the boat, Neo
// and Chats, the boards, the web-only "Boat" screens, the manual search, Screen
// look, Profile and Sign out (the quick readings left in TOM-526). Home is the rail's
// first item and the tab bar's first tab, as the handoff lists it.
// It replaces the 288 px sidebar and the right-hand menu panel.
import { useEffect, useRef, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { boardIcon } from '../../lib/modeBoards';
import type { NeoBoardSummary } from '../../lib/neoBoards';
import type { NeoBoardsState } from '../../lib/useNeoBoards';
import { ScreenLookChoice } from '../ScreenLookChoice';
import { AppMark, Icon } from '../ui/Icon';
import { boatEntriesFor, BoatChipName, boardPath, boatPath, type AlarmBadge } from './shellNav';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function BoatSidebar({
  open,
  boatId,
  boatName,
  presenceDot,
  presenceLabel,
  ownerName,
  alarmBadge,
  boards,
  boatDetailsRef,
  boatDetailsLabel,
  boatDetailsDisabled,
  boatDetailsOpen,
  onBoatDetails,
  onSearch,
  onSignOut,
  onClose,
  guest = false,
}: {
  open: boolean;
  boatId: string;
  boatName: string;
  presenceDot: ReactNode;
  presenceLabel: string;
  ownerName: string;
  alarmBadge: AlarmBadge | null;
  boards: NeoBoardsState;
  boatDetailsRef: RefObject<HTMLButtonElement | null>;
  boatDetailsLabel: string;
  boatDetailsDisabled: boolean;
  boatDetailsOpen: boolean;
  onBoatDetails: (event: MouseEvent<HTMLButtonElement>) => void;
  onSearch: () => void;
  onSignOut: () => void;
  /** Dismissed by the scrim, the close button or Escape: focus goes back to the opener. */
  onClose: () => void;
  /** A Try it guest (TOM-868): the screens a guest cannot use are left out. */
  guest?: boolean;
}) {
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);

  // The shell re-renders every second (its clock), so onClose is a new function
  // each time; reading it through a ref keeps the effect to the moments the
  // sidebar opens and closes. Otherwise focus would jump back to Close each second.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      panel?.querySelector<HTMLElement>('.shell-close')?.focus({ preventScroll: true });
      // The row for the page on screen, scrolled into the panel's view, so the
      // open sidebar shows where you are (Alarms sits below the fold).
      panel
        ?.querySelector<HTMLElement>('[aria-current="page"]')
        ?.scrollIntoView({ block: 'nearest' });
    });
    const onKey = (e: KeyboardEvent) => {
      // The boat details dialog opens over the sidebar and owns Escape and
      // focus while it is open.
      if (document.querySelector('dialog[open]') !== null) return;
      const panel = panelRef.current;
      if (panel === null) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus stays inside the sidebar while it is open.
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;

  const to = (path: string) => boatPath(boatId, path);
  const systemBoards: NeoBoardSummary[] = boards.kind === 'ready' ? boards.systemBoards : [];
  const ownBoards: NeoBoardSummary[] = boards.kind === 'ready' ? boards.boards : [];

  return (
    <>
      <div className="shell-side-scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className="shell-side"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        data-testid="boat-menu"
      >
        <div className="shell-side__head">
          <AppMark size={30} />
          <span className="shell-side__brand">Neomaris</span>
          <button
            type="button"
            className="shell-close"
            aria-label="Close menu"
            data-testid="sidebar-close"
            onClick={onClose}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <button
          type="button"
          className="shell-search shell-side__search"
          data-testid="sidebar-search"
          onClick={onSearch}
        >
          <Icon name="search" size={16} />
          <span>Find a device manual</span>
        </button>

        <ul className="tb-list">
          <li>
            <button
              ref={boatDetailsRef}
              type="button"
              className="tb-list__row shell-side__boat"
              aria-haspopup="dialog"
              aria-expanded={boatDetailsOpen}
              aria-controls="boat-details-dialog"
              aria-label={boatDetailsLabel}
              disabled={boatDetailsDisabled}
              data-testid="boat-details-trigger"
              onClick={onBoatDetails}
            >
              <span className="tb-list__icon tb-list__icon--quiet">
                <Icon name="anchor" size={17} />
              </span>
              <span className="tb-list__body">
                <BoatChipName name={boatName} />
                <span className="shell-side__presence">
                  {presenceDot}
                  {presenceLabel}
                </span>
                <Icon name="chevron-right" size={16} className="shell-row__chevron" />
              </span>
            </button>
          </li>
        </ul>

        <ul className="tb-list">
          <SideRow
            to={to('neo')}
            label="Neo"
            detail="Main chat"
            testId="sidebar-neo"
            icon={<Icon name="tombolo" size={17} />}
          />
          <SideRow
            to={to('chats')}
            label="Chats"
            testId="sidebar-chats"
            icon={<Icon name="chats" size={17} />}
          />
        </ul>

        <p className="tb-section-label">Your boards</p>
        <ul className="tb-list" data-testid="sidebar-boards">
          {[...systemBoards, ...ownBoards].map((b) => {
            const path = to(boardPath(b.slug));
            return (
              <SideRow
                key={b.slug}
                to={path}
                label={b.title}
                testId={`sidebar-board-${b.slug}`}
                icon={<Icon name={boardIcon(b)} size={17} />}
                checked={pathname === path}
              />
            );
          })}
          <SideRow
            to={to('boards')}
            end
            label="All boards"
            testId="sidebar-all-boards"
            icon={<Icon name="boards" size={17} />}
          />
        </ul>

        <p className="tb-section-label">Boat</p>
        <ul className="tb-list">
          {boatEntriesFor(guest).map((e) => (
            <SideRow
              key={e.key}
              to={to(e.path)}
              label={e.label}
              testId={`sidebar-${e.key}`}
              icon={e.icon()}
              badge={
                e.key === 'alarms' && alarmBadge ? (
                  <span
                    className={`rail-badge rail-badge--${alarmBadge.tone}`}
                    data-testid="nav-alarms-badge-sidebar"
                  >
                    {alarmBadge.count}
                  </span>
                ) : null
              }
            />
          ))}
        </ul>

        <p className="tb-section-label">Screen look</p>
        <ScreenLookChoice className="shell-side__look" label="Screen look" />
        <ul className="tb-list shell-side__account">
          <SideRow
            to="/profile"
            label="Profile"
            detail={ownerName}
            testId="sidebar-profile"
            icon={<Icon name="user" size={17} />}
          />
          <SideRow
            to="/dashboard"
            end
            label="Your boats"
            testId="sidebar-boats"
            icon={<AppMark size={17} />}
          />
          <li>
            <button
              type="button"
              className="tb-list__row shell-side-row"
              data-testid="sidebar-sign-out"
              onClick={onSignOut}
            >
              <span className="tb-list__icon tb-list__icon--quiet">
                <Icon name="sign-out" size={17} />
              </span>
              <span className="tb-list__body">
                <span className="tb-list__title">Sign out</span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </>
  );
}

function SideRow({
  to,
  end,
  label,
  detail,
  icon,
  testId,
  badge,
  checked,
}: {
  to: string;
  end?: boolean;
  label: string;
  detail?: string;
  icon: ReactNode;
  testId?: string;
  badge?: ReactNode;
  /** The board on screen: a check, as the handoff's "Your boards" shows it. */
  checked?: boolean;
}) {
  return (
    <li>
      <NavLink to={to} end={end} className="tb-list__row shell-side-row" data-testid={testId}>
        <span className="tb-list__icon">{icon}</span>
        <span className="tb-list__body">
          <span className="tb-list__title">{label}</span>
          {detail ? <span className="tb-list__value shell-side__detail">{detail}</span> : null}
          {badge}
          {checked ? <Icon name="check" size={17} className="shell-row__check" /> : null}
        </span>
      </NavLink>
    </li>
  );
}

/** The header's menu button where there is no sidebar (768 px and under). */
export function SidebarButton({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="tb-icon-btn tb-icon-btn--glass shell-menu-btn"
      aria-label="Menu"
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid="boat-menu-button"
      onClick={onOpen}
    >
      <Icon name="menu" size={20} />
    </button>
  );
}
